import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Plus, Trash2, ChevronDown, ChevronRight, Sun, BatteryCharging, ArrowLeftRight, PlugZap, Camera, Loader2 } from 'lucide-react';
import { CONSUMER_TYPES, CABLE_OPTIONS, BREAKER_AMPS, BREAKER_CURVES, KA_OPTIONS, DIFF_MILLIAMPS, DIFF_AMPS, DIFF_TYPES, consumerLabel } from './symbols';
import api from '../../lib/api';
import { toast } from 'sonner';

const uid = () => Math.random().toString(36).slice(2, 10);

const PRODUCT_CAT = { omvormer: 'omvormers', pv_string: 'zonnepanelen', batterij: 'batterijen', laadpaal: 'laadpalen' };

export const TreePanel = ({ tree, onChange, products = [] }) => {
  const [collapsed, setCollapsed] = React.useState({});
  const [newDiffParent, setNewDiffParent] = React.useState('teller');
  const diffs = tree?.differentials || [];

  const update = (newDiffs) => onChange({ ...tree, differentials: newDiffs });

  const nextCircuitNo = () => {
    const all = diffs.flatMap(d => (d.circuits || []).map(c => parseInt(c.circuit_no) || 0));
    return (all.length ? Math.max(...all) : 0) + 1;
  };

  const addDiff = (device = 'diff', parentOverride) => {
    update([...diffs, {
      id: uid(),
      device,
      name: device === 'auto'
        ? `Auto ${diffs.filter(x => x.device === 'auto').length + 1}`
        : `Diff ${diffs.filter(x => x.device !== 'auto').length + 1}`,
      diff_type: 'A',
      milliamp: diffs.length === 0 ? 300 : 30, amperage: 40, curve: 'C',
      poles: diffs.length === 0 ? 4 : 2, ka: '3000',
      parent_id: parentOverride !== undefined ? parentOverride : (newDiffParent === 'teller' ? null : newDiffParent),
      circuits: []
    }]);
  };
  const setDiff = (di, patch) => update(diffs.map((d, i) => i === di ? { ...d, ...patch } : d));
  const delDiff = (di) => {
    const removedId = diffs[di].id;
    update(diffs.filter((_, i) => i !== di).map(d => d.parent_id === removedId ? { ...d, parent_id: null } : d));
  };

  const isThreePhase = (tree?.supply?.voltage || '230V') !== '230V';

  const addSpecialCircuit = (di, kind) => {
    const tp = isThreePhase;
    const g = tp ? '5G' : '3G';
    const base = { id: uid(), circuit_no: nextCircuitNo(), curve: 'C', ka: '3000', poles: tp ? 4 : 2, phase: tp ? 'drie' : 'mono', kind };
    let circuit;
    if (kind === 'pv') {
      circuit = { ...base, label: 'PV', amperage: tp ? 16 : 25, cable: tp ? 'XGB Cca 5G2,5' : 'XGB Cca 3G4', consumers: [{ type: 'omvormer', count: 1 }, { type: 'pv_string', count: 1, cable: 'Solar 2x4 + PE 1x4' }] };
    } else if (kind === 'rem') {
      circuit = { ...base, label: 'REM', amperage: 40, cable: '', consumers: [], subcircuits: [] };
    } else if (kind === 'laadpaal') {
      circuit = { ...base, label: 'LP', amperage: 32, cable: `XGB Cca ${g}6`, consumers: [{ type: 'laadpaal', count: 1 }] };
    } else if (kind === 'verdeelbord') {
      circuit = { ...base, label: 'VB', amperage: 40, cable: `XGB Cca ${g}10`, consumers: [{ type: 'verdeelbord', count: 1 }] };
    } else {
      circuit = { ...base, label: '', amperage: tp ? 16 : 25, cable: `XGB Cca ${g}4`, consumers: [{ type: 'batterij', count: 1, cable: '3G4' }] };
    }
    setDiff(di, { circuits: [...(diffs[di].circuits || []), circuit] });
  };

  const ampsForKind = (kind, poles) => {
    if (kind === 'rem' || kind === 'verdeelbord') return poles >= 3 ? [25, 32, 40, 63] : [40, 63];
    if (kind === 'laadpaal') return poles >= 3 ? [16, 20, 25, 32, 40] : [20, 25, 32, 40];
    if (kind === 'pv' || kind === 'batterij') return poles >= 3 ? [10, 16, 20, 25] : [25, 20];
    return BREAKER_AMPS;
  };
  const ampOptionsFor = (c) => ampsForKind(c.kind, c.poles);
  const CABLE_TYPES = ['XVB', 'XGB Cca', 'EXVB'];
  const cableOptionsFor = (c) => {
    // kabeltype (XVB/XGB/EXVB) \u00d7 3G/5G zodat mono- en driefasig altijd te kiezen zijn
    if (c.kind === 'rem' || c.kind === 'verdeelbord') return CABLE_TYPES.flatMap(t => ['3G', '5G'].flatMap(gg => ['6', '10', '16'].map(sec => `${t} ${gg}${sec}`)));
    if (c.kind === 'laadpaal') return CABLE_TYPES.flatMap(t => ['3G', '5G'].map(gg => `${t} ${gg}6`));
    if (c.kind) return CABLE_TYPES.flatMap(t => ['3G', '5G'].flatMap(gg => ['2,5', '4', '6', '10'].map(sec => `${t} ${gg}${sec}`)));
    return CABLE_OPTIONS;
  };
  const SUB_CABLES = CABLE_TYPES.flatMap(t => ['3G', '5G'].flatMap(gg => ['2,5', '4', '6', '10'].map(sec => `${t} ${gg}${sec}`)));
  const setSubcircuit = (di, ci, sci, patch) => setCircuit(di, ci, { subcircuits: (diffs[di].circuits[ci].subcircuits || []).map((s, i) => i === sci ? { ...s, ...patch } : s) });
  const delSubcircuit = (di, ci, sci) => {
    const sid = diffs[di].circuits[ci].subcircuits[sci].id;
    update(diffs.map((d, i) => {
      const base = String(d.parent_id || '') === `vb:${sid}` ? { ...d, parent_id: null } : d;
      return i === di ? { ...base, circuits: base.circuits.map((cc, j) => j === ci ? { ...cc, subcircuits: (cc.subcircuits || []).filter((_, k2) => k2 !== sci) } : cc) } : base;
    }));
  };
  const addSubcircuit = (di, ci, kind) => {
    const tp = isThreePhase;
    const gg = tp ? '5G' : '3G';
    const sub = kind === 'laadpaal'
      ? { id: uid(), kind: 'laadpaal', label: 'LP', amperage: 32, curve: 'C', poles: tp ? 4 : 2, phase: tp ? 'drie' : 'mono', cable: `XGB Cca ${gg}6`, brand: '', model: '', sn: '' }
      : { id: uid(), kind: '', label: '', amperage: 16, curve: 'C', poles: tp ? 4 : 2, phase: tp ? 'drie' : 'mono', cable: `XGB Cca ${gg}2,5`, type: 'stopcontact' };
    setCircuit(di, ci, { subcircuits: [...(diffs[di].circuits[ci].subcircuits || []), sub] });
  };
  const setCircuit = (di, ci, patch) => setDiff(di, { circuits: diffs[di].circuits.map((c, i) => i === ci ? { ...c, ...patch } : c) });

  const pressTimer = React.useRef(null);
  const startPress = (text) => {
    if (!text) return;
    pressTimer.current = setTimeout(async () => {
      try {
        await navigator.clipboard.writeText(text);
        toast.success(`Serienummer gekopieerd: ${text}`);
      } catch (e) { /* stil */ }
    }, 550);
  };
  const endPress = () => { if (pressTimer.current) clearTimeout(pressTimer.current); };

  const fileRef = React.useRef(null);
  const scanTarget = React.useRef(null);
  const [scanning, setScanning] = React.useState(false);
  const startScan = (target) => { scanTarget.current = target; fileRef.current?.click(); };
  const onScanFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !scanTarget.current) return;
    setScanning(true);
    toast.info('Serienummer scannen...');
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.post('/schemas/scan-serial', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 });
      const sn = res.data?.serial;
      const { di, ci, coi, sbi, sci } = scanTarget.current;
      if (sn) {
        if (sci != null) {
          setSubcircuit(di, ci, sci, { sn });
        } else if (sbi != null) {
          const co = diffs[di].circuits[ci].consumers[coi];
          setConsumer(di, ci, coi, { subbatteries: (co.subbatteries || []).map((s2, i2) => i2 === sbi ? { ...s2, sn } : s2) });
        } else {
          setConsumer(di, ci, coi, { sn });
        }
        toast.success(`Serienummer gescand: ${sn}`);
      } else {
        toast.error('Geen serienummer gevonden op de foto');
      }
    } catch (err) {
      toast.error('Scannen mislukt');
    } finally {
      setScanning(false);
    }
  };
  const delCircuit = (di, ci) => {
    const cid = diffs[di].circuits[ci].id;
    const newCircuits = diffs[di].circuits.filter((_, i) => i !== ci);
    update(diffs.map((d, i) => {
      const base = String(d.parent_id || '') === `vb:${cid}` ? { ...d, parent_id: null } : d;
      return i === di ? { ...base, circuits: newCircuits } : base;
    }));
  };

  const addConsumer = (di, ci, type = 'stopcontact') => {
    const extra = type === 'pv_string' ? { cable: 'Solar 2x4 + PE 1x4' } : (type === 'batterij' ? { cable: '3G4' } : {});
    setCircuit(di, ci, { consumers: [...(diffs[di].circuits[ci].consumers || []), { type, count: 1, ...extra }] });
  };
  const setConsumer = (di, ci, coi, patch) => setCircuit(di, ci, { consumers: diffs[di].circuits[ci].consumers.map((co, i) => i === coi ? { ...co, ...patch } : co) });
  const delConsumer = (di, ci, coi) => {
    const c = diffs[di].circuits[ci];
    const wasVb = c.consumers[coi]?.type === 'verdeelbord';
    update(diffs.map((d, i) => {
      const base = (wasVb && String(d.parent_id || '') === `vb:${c.id}`) ? { ...d, parent_id: null } : d;
      return i === di ? { ...base, circuits: base.circuits.map((cc, j) => j === ci ? { ...cc, consumers: cc.consumers.filter((_, k) => k !== coi) } : cc) } : base;
    }));
  };

  const productPick = (di, ci, coi, productId) => {
    const p = products.find(x => x.id === productId);
    if (!p) return;
    const upd = { brand: p.brand || '', model: p.model || p.name || '' };
    const co = diffs[di].circuits[ci].consumers[coi];
    if (co?.type === 'pv_string') {
      const m = `${p.specifications || ''} ${p.name || ''}`.match(/(\d{3,4})\s*Wp/i);
      if (m) upd.wp = m[1];
    }
    setConsumer(di, ci, coi, upd);
  };

  const parentName = (d) => {
    if (!d.parent_id) return 'Teller';
    if (String(d.parent_id).startsWith('vb:')) {
      const cid = String(d.parent_id).slice(3);
      for (const dd of diffs) {
        for (const c of (dd.circuits || [])) {
          if (c.id === cid) return `na VB (${c.label || `kring ${c.circuit_no}`})`;
          const sc = (c.subcircuits || []).find(s => s.id === cid);
          if (sc) return `na VB (${sc.label || 'achter REM'})`;
        }
      }
      return 'na verdeelbord';
    }
    const p = diffs.find(x => x.id === d.parent_id);
    return p ? `na ${p.name}` : 'Teller';
  };

  return (
    <div className="space-y-3" data-testid="schema-tree-panel">
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onScanFile} data-testid="scan-sn-file-input" />
      {/* Voeding */}
      <div className="border border-border/60 rounded-lg p-3 space-y-2">
        <p className="text-xs font-semibold text-muted-foreground uppercase">Voeding / Teller</p>
        <div className="flex gap-2">
          <Input className="h-8 text-sm" value={tree?.supply?.label || ''} placeholder="Teller"
            onChange={e => onChange({ ...tree, supply: { ...tree.supply, label: e.target.value } })} data-testid="supply-label-input" />
          <Select value={tree?.supply?.voltage || '230V'} onValueChange={v => onChange({ ...tree, supply: { ...tree.supply, voltage: v } })}>
            <SelectTrigger className="h-8 w-28 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="230V">230V</SelectItem>
              <SelectItem value="3×230V">3×230V</SelectItem>
              <SelectItem value="3N400V">3N400V</SelectItem>
            </SelectContent>
          </Select>
          <Select value={String(tree?.supply?.amperage || 40)} onValueChange={v => onChange({ ...tree, supply: { ...tree.supply, amperage: parseInt(v) } })}>
            <SelectTrigger className="h-8 w-24 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{[25, 32, 40, 50, 63, 80].map(a => <SelectItem key={a} value={String(a)}>{a}A</SelectItem>)}</SelectContent>
          </Select>
          <Select value={tree?.supply?.cable || '4G10'} onValueChange={v => onChange({ ...tree, supply: { ...tree.supply, cable: v } })}>
            <SelectTrigger className="h-8 w-28 text-sm" data-testid="supply-cable-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CABLE_OPTIONS.map(cb => <SelectItem key={cb} value={cb}>{cb}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
          <input type="checkbox" className="accent-orange-500" checked={tree?.show_kwh !== false}
            onChange={e => {
              const on = e.target.checked;
              const t2 = { ...tree, show_kwh: on };
              if (on) {
                // terug aanzetten = ook individueel verwijderde meters herstellen
                t2.show_kwh_main = true;
                t2.differentials = diffs.map(d => ({ ...d, circuits: (d.circuits || []).map(c => ({ ...c, show_kwh: true })) }));
              }
              onChange(t2);
            }} data-testid="toggle-kwh-meters" />
          kWh-meters automatisch tekenen (bij thuisbatterij)
        </label>
      </div>

      {diffs.map((d, di) => (
        <div key={d.id} className={`border rounded-lg p-3 space-y-2 ${d.parent_id ? 'border-blue-500/30 ml-4' : 'border-orange-500/30'}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" onClick={() => setCollapsed(p => ({ ...p, [d.id]: !p[d.id] }))} className="text-muted-foreground">
              {collapsed[d.id] ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            <Input className="h-8 text-sm w-24 flex-shrink-0" value={d.name} onChange={e => setDiff(di, { name: e.target.value })} data-testid={`diff-name-${di}`} />
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">{parentName(d)}</span>
            {d.device === 'auto' ? (
              <Select value={d.curve || 'C'} onValueChange={v => setDiff(di, { curve: v })}>
                <SelectTrigger className="h-8 w-16 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{BREAKER_CURVES.map(cv => <SelectItem key={cv} value={cv}>{cv}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
              <>
                <Select value={d.diff_type || 'A'} onValueChange={v => setDiff(di, { diff_type: v })}>
                  <SelectTrigger className="h-8 w-20 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIFF_TYPES.map(t => <SelectItem key={t} value={t}>Type {t}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={String(d.milliamp)} onValueChange={v => setDiff(di, { milliamp: parseInt(v) })}>
                  <SelectTrigger className="h-8 w-24 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{DIFF_MILLIAMPS.map(m => <SelectItem key={m} value={String(m)}>{m}mA</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}
            <Select value={String(d.amperage)} onValueChange={v => setDiff(di, { amperage: parseInt(v) })}>
              <SelectTrigger className="h-8 w-20 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{(d.device === 'auto' ? BREAKER_AMPS : DIFF_AMPS).map(a => <SelectItem key={a} value={String(a)}>{a}A</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(d.poles)} onValueChange={v => setDiff(di, { poles: parseInt(v) })}>
              <SelectTrigger className="h-8 w-18 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="2">2P</SelectItem><SelectItem value="4">4P</SelectItem></SelectContent>
            </Select>
            <Select value={d.ka || '3000'} onValueChange={v => setDiff(di, { ka: v })}>
              <SelectTrigger className="h-8 w-24 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>{KA_OPTIONS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => delDiff(di)} data-testid={`del-diff-${di}`}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          {!collapsed[d.id] && (
            <div className="pl-4 space-y-2">
              {(d.circuits || []).map((c, ci) => (
                <div key={c.id} className="border border-border/50 rounded-md p-2 space-y-2 bg-muted/20">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border-2 border-orange-500 text-orange-500 text-xs font-bold flex-shrink-0">{c.circuit_no}</span>
                    <Input className="h-8 text-sm flex-1 min-w-[120px]" placeholder="Kringnaam" value={c.label}
                      onChange={e => setCircuit(di, ci, { label: e.target.value })} data-testid={`circuit-label-${di}-${ci}`} />
                    {c.kind === 'rem' ? (
                      <Select value={String(c.poles || 4)} onValueChange={v => setCircuit(di, ci, { poles: parseInt(v) })}>
                        <SelectTrigger className="h-8 w-24 text-sm" data-testid={`rem-poles-${di}-${ci}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="2">2-polig</SelectItem><SelectItem value="3">3-polig</SelectItem><SelectItem value="4">4-polig</SelectItem></SelectContent>
                      </Select>
                    ) : c.kind && (
                      <Select value={String(c.poles || 2)} onValueChange={v => {
                        const poles = parseInt(v);
                        const gg = poles >= 3 ? '5G' : '3G';
                        const DEFAULT_AMP = { pv: { mono: 25, drie: 16 }, batterij: { mono: 25, drie: 16 }, laadpaal: { mono: 32, drie: 32 } };
                        const ph = poles >= 3 ? 'drie' : 'mono';
                        const patch = { poles, phase: ph, amperage: DEFAULT_AMP[c.kind]?.[ph] || c.amperage };
                        patch.cable = `XGB Cca ${gg}${c.kind === 'laadpaal' ? '6' : (c.kind === 'verdeelbord' ? '10' : (c.kind === 'pv' && poles >= 3 ? '2,5' : '4'))}`;
                        setCircuit(di, ci, patch);
                      }}>
                        <SelectTrigger className="h-8 w-26 text-sm" data-testid={`poles-select-${di}-${ci}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="2">2-polig</SelectItem><SelectItem value="3">3-polig</SelectItem><SelectItem value="4">4-polig</SelectItem></SelectContent>
                      </Select>
                    )}
                    <Select value={String(c.amperage)} onValueChange={v => setCircuit(di, ci, { amperage: parseInt(v) })}>
                      <SelectTrigger className="h-8 w-20 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{ampOptionsFor(c).map(a => <SelectItem key={a} value={String(a)}>{a}A</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={c.curve || 'C'} onValueChange={v => setCircuit(di, ci, { curve: v })}>
                      <SelectTrigger className="h-8 w-16 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{BREAKER_CURVES.map(cv => <SelectItem key={cv} value={cv}>{cv}</SelectItem>)}</SelectContent>
                    </Select>
                    {!c.kind && (
                      <Select value={String(c.poles || 2)} onValueChange={v => setCircuit(di, ci, { poles: parseInt(v) })}>
                        <SelectTrigger className="h-8 w-16 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="2">2P</SelectItem><SelectItem value="4">4P</SelectItem></SelectContent>
                      </Select>
                    )}
                    <Select value={c.ka || '3000'} onValueChange={v => setCircuit(di, ci, { ka: v })}>
                      <SelectTrigger className="h-8 w-22 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>{KA_OPTIONS.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={c.cable || '_none'} onValueChange={v => setCircuit(di, ci, { cable: v === '_none' ? '' : v })}>
                      <SelectTrigger className="h-8 w-32 text-sm" data-testid={`circuit-cable-${di}-${ci}`}><SelectValue placeholder="Kabel" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Geen kabel</SelectItem>
                        {cableOptionsFor(c).map(cb => <SelectItem key={cb} value={cb}>{cb}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => delCircuit(di, ci)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {c.kind === 'laadpaal' && (
                    <div className="flex items-center gap-1.5 flex-wrap pl-9">
                      <span className="text-[10px] text-muted-foreground">Differentieel vóór automaat:</span>
                      <Select value={c.lp_diff_type || 'A'} onValueChange={v => setCircuit(di, ci, { lp_diff_type: v })}>
                        <SelectTrigger className="h-7 w-20 text-xs" data-testid={`lp-diff-type-${di}-${ci}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{DIFF_TYPES.map(t => <SelectItem key={t} value={t}>Diff {t}</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={String(c.lp_diff_ma || 30)} onValueChange={v => setCircuit(di, ci, { lp_diff_ma: parseInt(v) })}>
                        <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{DIFF_MILLIAMPS.map(m => <SelectItem key={m} value={String(m)}>{m}mA</SelectItem>)}</SelectContent>
                      </Select>
                      <Select value={String(c.lp_diff_amp || 40)} onValueChange={v => setCircuit(di, ci, { lp_diff_amp: parseInt(v) })}>
                        <SelectTrigger className="h-7 w-18 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{[40, 63].map(a => <SelectItem key={a} value={String(a)}>{a}A</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  )}
                  {(c.kind !== 'rem' || (c.consumers || []).length > 0) && (
                  <div className="pl-9 space-y-1.5">
                    {(c.consumers || []).map((co, coi) => (
                      <div key={coi} className="space-y-1 border-l-2 border-border/40 pl-2">
                        <div className="flex items-center gap-2">
                          {c.kind ? (
                            <span className="h-7 flex-1 text-xs flex items-center px-2 border border-border/50 rounded-md bg-muted/40">{consumerLabel(co.type)}</span>
                          ) : (
                            <Select value={co.type} onValueChange={v => setConsumer(di, ci, coi, { type: v })}>
                              <SelectTrigger className="h-7 flex-1 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>{CONSUMER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                            </Select>
                          )}
                          {!['kabel', 'laadpaal', 'verdeelbord'].includes(co.type) && (
                            <Input type="number" min={1} className="h-7 w-14 text-xs" value={co.count}
                              onChange={e => setConsumer(di, ci, coi, { count: Math.max(1, parseInt(e.target.value) || 1) })} />
                          )}
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => delConsumer(di, ci, coi)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                        {co.type === 'kabel' && (
                          <div className="flex gap-1.5">
                            <Select value="_pick" onValueChange={v => v !== '_pick' && setConsumer(di, ci, coi, { specs: v })}>
                              <SelectTrigger className="h-7 w-28 text-xs"><SelectValue placeholder="Kies" /></SelectTrigger>
                              <SelectContent>{CABLE_OPTIONS.map(cb => <SelectItem key={cb} value={cb}>{cb}</SelectItem>)}</SelectContent>
                            </Select>
                            <Input className="h-7 text-xs flex-1" placeholder="Kabeltype (bv. Solar 2x4 + PE 1x4)"
                              value={co.specs || ''} onChange={e => setConsumer(di, ci, coi, { specs: e.target.value })}
                              data-testid={`consumer-cable-${di}-${ci}-${coi}`} />
                          </div>
                        )}
                        {['omvormer', 'pv_string', 'batterij', 'backup', 'laadpaal'].includes(co.type) && (
                          <div className="space-y-1">
                            {PRODUCT_CAT[co.type] && (
                              <Select value="_pick" onValueChange={v => v !== '_pick' && productPick(di, ci, coi, v)}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`product-pick-${di}-${ci}-${coi}`}>
                                  <SelectValue placeholder="Kies uit producten..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.filter(p => p.category === PRODUCT_CAT[co.type]).map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            <div className="flex gap-1.5">
                              <Input className="h-7 text-xs w-24" placeholder="Merk" value={co.brand || ''}
                                onChange={e => setConsumer(di, ci, coi, { brand: e.target.value })} />
                              <Input className="h-7 text-xs flex-1" placeholder="Type" value={co.model || ''}
                                onChange={e => setConsumer(di, ci, coi, { model: e.target.value })} />
                              {co.type === 'pv_string' ? (
                                <Input type="number" className="h-7 text-xs w-20" placeholder="Wp" value={co.wp || ''}
                                  onChange={e => setConsumer(di, ci, coi, { wp: e.target.value })} data-testid={`pv-wp-${di}-${ci}-${coi}`} />
                              ) : (c.kind === 'batterij' && co.type === 'omvormer') ? null : (
                                <div className="flex gap-1 items-center flex-1 min-w-0">
                                  <Input className="h-7 text-xs flex-1 min-w-0 select-none" placeholder="Serienr (lang indrukken = kopieer)" value={co.sn || ''}
                                    onChange={e => setConsumer(di, ci, coi, { sn: e.target.value })}
                                    onTouchStart={() => startPress(co.sn)} onTouchEnd={endPress} onTouchMove={endPress}
                                    onMouseDown={() => startPress(co.sn)} onMouseUp={endPress} onMouseLeave={endPress}
                                    data-testid={`sn-${di}-${ci}-${coi}`} />
                                  <Button variant="outline" size="icon" className="h-7 w-7 flex-shrink-0" disabled={scanning}
                                    onClick={() => startScan({ di, ci, coi })} title="Scan serienummer met camera/foto"
                                    data-testid={`scan-sn-${di}-${ci}-${coi}`}>
                                    {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                  </Button>
                                </div>
                              )}
                            </div>
                            {(co.type === 'pv_string' || co.type === 'batterij') && (
                              <Input className="h-7 text-xs" placeholder="Kabel (type, sectie, geleiders)" value={co.cable || ''}
                                onChange={e => setConsumer(di, ci, coi, { cable: e.target.value })}
                                data-testid={`consumer-cable-field-${di}-${ci}-${coi}`} />
                            )}
                            {co.type === 'batterij' && (
                              <div className="space-y-1 pl-2 border-l border-border/40">
                                {(co.subbatteries || []).map((sb, sbi) => (
                                  <div key={sbi} className="flex items-center gap-1.5 flex-wrap">
                                    <span className="text-[10px] text-muted-foreground w-7 flex-shrink-0">B{sbi + 1}</span>
                                    <Input className="h-7 text-xs w-20" placeholder="Merk" value={sb.brand || ''}
                                      onChange={e => setConsumer(di, ci, coi, { subbatteries: (co.subbatteries || []).map((s2, i2) => i2 === sbi ? { ...s2, brand: e.target.value } : s2) })} />
                                    <Select value={sb.model || ''} onValueChange={v => setConsumer(di, ci, coi, { subbatteries: (co.subbatteries || []).map((s2, i2) => i2 === sbi ? { ...s2, model: v } : s2) })}>
                                      <SelectTrigger className="h-7 text-xs w-36" data-testid={`subbat-type-${di}-${ci}-${coi}-${sbi}`}><SelectValue placeholder="Type (uit producten)" /></SelectTrigger>
                                      <SelectContent>
                                        {products.filter(p => p.category === 'batterijen').map(p => (
                                          <SelectItem key={p.id} value={p.model || p.name}>{p.name}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Input type="number" step="0.5" className="h-7 text-xs w-16" placeholder="kWh" value={sb.kwh ?? '5'}
                                      onChange={e => setConsumer(di, ci, coi, { subbatteries: (co.subbatteries || []).map((s2, i2) => i2 === sbi ? { ...s2, kwh: e.target.value } : s2) })}
                                      data-testid={`subbat-kwh-${di}-${ci}-${coi}-${sbi}`} />
                                    <Input className="h-7 text-xs flex-1 min-w-[110px]" placeholder="Serienummer" value={sb.sn || ''}
                                      onChange={e => setConsumer(di, ci, coi, { subbatteries: (co.subbatteries || []).map((s2, i2) => i2 === sbi ? { ...s2, sn: e.target.value } : s2) })}
                                      data-testid={`subbat-sn-${di}-${ci}-${coi}-${sbi}`} />
                                    <Button variant="outline" size="icon" className="h-7 w-7 flex-shrink-0" disabled={scanning}
                                      onClick={() => startScan({ di, ci, coi, sbi })} title="Scan serienummer met camera/foto"
                                      data-testid={`subbat-scan-${di}-${ci}-${coi}-${sbi}`}>
                                      {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                      onClick={() => setConsumer(di, ci, coi, { subbatteries: (co.subbatteries || []).filter((_, i2) => i2 !== sbi) })}>
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                                <Button variant="outline" size="sm" className="h-6 text-[10px] px-2"
                                  onClick={() => setConsumer(di, ci, coi, { subbatteries: [...(co.subbatteries || []), { kwh: '5' }] })}
                                  data-testid={`add-subbat-${di}-${ci}-${coi}`}>
                                  <Plus className="w-3 h-3 mr-1" /> Subbatterij
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                        {co.type === 'backup' && (
                          <div className="flex gap-1.5 flex-wrap items-center pl-1">
                            <span className="text-[10px] text-muted-foreground">Naar verdeelbord:</span>
                            <Select value={co.ats_cable || (isThreePhase ? 'XVB 5G4' : 'XVB 3G4')} onValueChange={v => setConsumer(di, ci, coi, { ats_cable: v })}>
                              <SelectTrigger className="h-7 w-28 text-xs" data-testid={`ats-cable-${di}-${ci}-${coi}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{CABLE_TYPES.flatMap(t => ['2,5', '4', '6', '10'].map(sec => `${t} ${isThreePhase ? '5G' : '3G'}${sec}`)).map(cb => <SelectItem key={cb} value={cb}>{cb}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={co.ats_diff_type || 'A'} onValueChange={v => setConsumer(di, ci, coi, { ats_diff_type: v })}>
                              <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>{DIFF_TYPES.map(t => <SelectItem key={t} value={t}>Diff {t}</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={String(co.ats_diff_ma || 30)} onValueChange={v => setConsumer(di, ci, coi, { ats_diff_ma: parseInt(v) })}>
                              <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>{DIFF_MILLIAMPS.map(m => <SelectItem key={m} value={String(m)}>{m}mA</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={String(co.ats_amp || 20)} onValueChange={v => setConsumer(di, ci, coi, { ats_amp: parseInt(v) })}>
                              <SelectTrigger className="h-7 w-18 text-xs" data-testid={`ats-amp-${di}-${ci}-${coi}`}><SelectValue /></SelectTrigger>
                              <SelectContent>{[10, 16, 20, 25, 32, 40].map(a => <SelectItem key={a} value={String(a)}>{a}A</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        {!['omvormer', 'pv_string', 'batterij', 'backup', 'kabel', 'laadpaal', 'verdeelbord'].includes(co.type) && (
                          <Input className="h-7 text-xs" placeholder="Specs (optioneel)"
                            value={co.specs || ''} onChange={e => setConsumer(di, ci, coi, { specs: e.target.value })}
                            data-testid={`consumer-specs-${di}-${ci}-${coi}`} />
                        )}
                        {co.type === 'omvormer' && (
                          <div className="flex gap-1.5">
                            <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => addConsumer(di, ci, 'pv_string')} data-testid={`quick-pv-${di}-${ci}-${coi}`}>
                              <Sun className="w-3 h-3 mr-1" /> + PV string
                            </Button>
                            <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => addConsumer(di, ci, 'batterij')} data-testid={`quick-bat-${di}-${ci}-${coi}`}>
                              <BatteryCharging className="w-3 h-3 mr-1" /> + Batterij
                            </Button>
                            <Button variant="outline" size="sm" className="h-6 text-[10px] px-2" onClick={() => addConsumer(di, ci, 'backup')} data-testid={`quick-backup-${di}-${ci}-${coi}`}>
                              <ArrowLeftRight className="w-3 h-3 mr-1" /> + Back-up
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                    {c.kind ? (
                      <div className="flex gap-1.5 flex-wrap">
                        {c.kind === 'verdeelbord' && (
                          <>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addDiff('diff', `vb:${c.id}`)} data-testid={`vb-add-diff-${di}-${ci}`}>
                              <Plus className="w-3 h-3 mr-1" /> Differentieel op verdeelbord
                            </Button>
                            <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addDiff('auto', `vb:${c.id}`)} data-testid={`vb-add-auto-${di}-${ci}`}>
                              <Plus className="w-3 h-3 mr-1" /> Automaat op verdeelbord
                            </Button>
                          </>
                        )}
                        {c.kind !== 'verdeelbord' && (
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addConsumer(di, ci, 'backup')} data-testid={`add-ats-${di}-${ci}`}>
                            <ArrowLeftRight className="w-3 h-3 mr-1" /> ATS (back-up) toevoegen
                          </Button>
                        )}
                        {c.kind === 'batterij' && (
                          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addConsumer(di, ci, 'pv_string')} data-testid={`add-pv-string-${di}-${ci}`}>
                            <Sun className="w-3 h-3 mr-1" /> PV string toevoegen
                          </Button>
                        )}
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addConsumer(di, ci)} data-testid={`add-consumer-${di}-${ci}`}>
                        <Plus className="w-3 h-3 mr-1" /> Component
                      </Button>
                    )}
                  </div>
                  )}
                  {c.kind === 'rem' && (
                    <div className="pl-9 space-y-2">
                      {(c.subcircuits || []).map((sc, sci) => (
                        <div key={sc.id} className="border border-border/40 rounded-md p-2 space-y-1.5 bg-muted/10" data-testid={`rem-sub-${di}-${ci}-${sci}`}>
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-[10px] font-bold text-muted-foreground flex-shrink-0">{sc.kind === 'laadpaal' ? 'LAADPAAL' : 'KRING'}</span>
                            {sc.kind !== 'laadpaal' && (
                              <Input className="h-7 w-24 text-xs" placeholder="Naam" value={sc.label || ''}
                                onChange={e => setSubcircuit(di, ci, sci, { label: e.target.value })} />
                            )}
                            <Select value={String(sc.amperage || 32)} onValueChange={v => setSubcircuit(di, ci, sci, { amperage: parseInt(v) })}>
                              <SelectTrigger className="h-7 w-18 text-xs"><SelectValue /></SelectTrigger>
                              <SelectContent>{[10, 16, 20, 25, 32, 40].map(a => <SelectItem key={a} value={String(a)}>{a}A</SelectItem>)}</SelectContent>
                            </Select>
                            <Select value={String(sc.poles || 2)} onValueChange={v => {
                              const poles = parseInt(v);
                              const gg = poles >= 3 ? '5G' : '3G';
                              setSubcircuit(di, ci, sci, { poles, phase: poles >= 3 ? 'drie' : 'mono', cable: `XGB Cca ${gg}${sc.kind === 'laadpaal' ? '6' : '2,5'}` });
                            }}>
                              <SelectTrigger className="h-7 w-24 text-xs" data-testid={`rem-sub-poles-${di}-${ci}-${sci}`}><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="2">2-polig</SelectItem><SelectItem value="3">3-polig</SelectItem><SelectItem value="4">4-polig</SelectItem></SelectContent>
                            </Select>
                            <Select value={sc.cable || ''} onValueChange={v => setSubcircuit(di, ci, sci, { cable: v })}>
                              <SelectTrigger className="h-7 w-32 text-xs"><SelectValue placeholder="Kabel" /></SelectTrigger>
                              <SelectContent>{SUB_CABLES.map(cb => <SelectItem key={cb} value={cb}>{cb}</SelectItem>)}</SelectContent>
                            </Select>
                            {sc.kind !== 'laadpaal' && (
                              <Select value={sc.type || 'stopcontact'} onValueChange={v => setSubcircuit(di, ci, sci, { type: v })}>
                                <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>{CONSUMER_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                              </Select>
                            )}
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => delSubcircuit(di, ci, sci)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                          {sc.kind === 'laadpaal' && (
                            <div className="space-y-1">
                              <Select value="_pick" onValueChange={v => { const p = products.find(x => x.id === v); if (p) setSubcircuit(di, ci, sci, { brand: p.brand || '', model: p.model || p.name || '' }); }}>
                                <SelectTrigger className="h-7 text-xs" data-testid={`rem-lp-product-${di}-${ci}-${sci}`}>
                                  <SelectValue placeholder="Kies laadpaal uit producten..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {products.filter(p => p.category === 'laadpalen').map(p => (
                                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <div className="flex gap-1.5">
                                <Input className="h-7 text-xs w-24" placeholder="Merk" value={sc.brand || ''}
                                  onChange={e => setSubcircuit(di, ci, sci, { brand: e.target.value })} />
                                <Input className="h-7 text-xs flex-1" placeholder="Type" value={sc.model || ''}
                                  onChange={e => setSubcircuit(di, ci, sci, { model: e.target.value })} />
                                <Input className="h-7 text-xs flex-1" placeholder="Serienummer" value={sc.sn || ''}
                                  onChange={e => setSubcircuit(di, ci, sci, { sn: e.target.value })} />
                                <Button variant="outline" size="icon" className="h-7 w-7 flex-shrink-0" disabled={scanning}
                                  onClick={() => startScan({ di, ci, sci })} title="Scan serienummer met camera/foto"
                                  data-testid={`rem-lp-scan-${di}-${ci}-${sci}`}>
                                  {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      <div className="flex gap-1.5">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addSubcircuit(di, ci, 'laadpaal')} data-testid={`rem-add-laadpaal-${di}-${ci}`}>
                          <PlugZap className="w-3 h-3 mr-1" /> Laadpaal achter REM
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => addSubcircuit(di, ci, '')} data-testid={`rem-add-kring-${di}-${ci}`}>
                          <Plus className="w-3 h-3 mr-1" /> Kring achter REM
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => addSpecialCircuit(di, 'pv')} data-testid={`add-pv-circuit-${di}`}>
                  <Sun className="w-3.5 h-3.5 mr-1" /> PV toevoegen
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => addSpecialCircuit(di, 'rem')} data-testid={`add-rem-circuit-${di}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> REM toevoegen
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => addSpecialCircuit(di, 'batterij')} data-testid={`add-batterij-circuit-${di}`}>
                  <BatteryCharging className="w-3.5 h-3.5 mr-1" /> Thuisbatterij toevoegen
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => addSpecialCircuit(di, 'laadpaal')} data-testid={`add-laadpaal-circuit-${di}`}>
                  <PlugZap className="w-3.5 h-3.5 mr-1" /> Laadpaal toevoegen
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => addSpecialCircuit(di, 'verdeelbord')} data-testid={`add-verdeelbord-circuit-${di}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Verdeelbord toevoegen
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Nieuwe differentieel + plaatsingskeuze */}
      <div className="flex gap-2">
        {diffs.length > 0 && (
          <Select value={newDiffParent} onValueChange={setNewDiffParent}>
            <SelectTrigger className="h-9 w-40 text-sm" data-testid="new-diff-parent-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="teller">Op teller</SelectItem>
              {diffs.map(d => <SelectItem key={d.id} value={d.id}>Na {d.name}</SelectItem>)}
              {diffs.flatMap(d => (d.circuits || []).flatMap(c => ([
                ...((c.consumers || []).some(co => co.type === 'verdeelbord') ? [{ id: c.id, label: c.label || `kring ${c.circuit_no}` }] : []),
                ...((c.subcircuits || []).filter(sc => sc.kind !== 'laadpaal' && sc.type === 'verdeelbord').map(sc => ({ id: sc.id, label: sc.label || 'achter REM' }))),
              ]))).map(v => (
                <SelectItem key={`vb:${v.id}`} value={`vb:${v.id}`}>Na verdeelbord ({v.label})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Button onClick={() => addDiff('diff')} size="sm" className="flex-1" data-testid="add-differential-btn">
          <Plus className="w-4 h-4 mr-1" /> Differentieel toevoegen
        </Button>
        <Button onClick={() => addDiff('auto')} size="sm" variant="outline" className="flex-1" data-testid="add-automaat-btn">
          <Plus className="w-4 h-4 mr-1" /> Automaat toevoegen
        </Button>
      </div>
    </div>
  );
};

export default TreePanel;
